/**
 * ShaclViolationError — typed wrapper for JSON-RPC `-32001` returned by
 * cogo-mcp-gateway's `cogo.ontology.*` mutation tools.
 *
 * Phase 437.6 Option B (canonical SHACL pre-INSERT gate) means every
 * mutation tool returns `-32001` with `data.validationReport` (RDF/JS
 * shape — see `rdf-validate-shacl`'s `ValidationReport`). This module
 * normalizes that shape into `ShaclViolation[]` so UI code does not
 * re-traverse the RDF/JS report at every render.
 *
 * Usage from `mcp-mutation-client.ts`:
 *   const parsed = parseShaclViolationFromJsonRpcError(envelope.error, tool)
 *   if (parsed) throw parsed
 */

/** Single SHACL violation, flattened from the RDF/JS ValidationReport. */
export interface ShaclViolation {
  focusNode: string
  resultPath: string | null
  message: string
  sourceShape: string | null
  /** Last segment of `sh:Violation` / `sh:Warning` / `sh:Info`. Defaults to "Violation". */
  severity: 'Violation' | 'Warning' | 'Info'
}

interface RpcErrorLike {
  code: number
  message: string
  data?: unknown
}

/**
 * Typed `Error` carrying the parsed SHACL violations + the tool that emitted
 * them. Toast renderer + ChatRail message kind both consume this directly.
 */
export class ShaclViolationError extends Error {
  readonly name = 'ShaclViolationError'
  readonly code = -32001
  readonly violations: readonly ShaclViolation[]
  readonly tool: string | null

  constructor(
    message: string,
    violations: readonly ShaclViolation[],
    opts?: { tool?: string },
  ) {
    super(message)
    this.violations = violations
    this.tool = opts?.tool ?? null
    // Maintain prototype chain across transpilation targets (esbuild + tsc).
    Object.setPrototypeOf(this, new.target.prototype)
  }
}

/** Type-guard. */
export function isShaclViolationError(x: unknown): x is ShaclViolationError {
  return x instanceof ShaclViolationError
}

/**
 * Parse a JSON-RPC error envelope into a ShaclViolationError. Returns null
 * when the error is not a SHACL violation (i.e. `code !== -32001`). The
 * shape is forgiving — missing fields collapse to empty violations + the
 * envelope's `message` string. This matches Option B's contract: when the
 * gate trips, the envelope carries enough to display *something*.
 */
export function parseShaclViolationFromJsonRpcError(
  err: RpcErrorLike,
  tool: string,
): ShaclViolationError | null {
  if (err.code !== -32001) return null
  const violations = extractViolations(err.data)
  return new ShaclViolationError(err.message, violations, { tool })
}

function extractViolations(data: unknown): ShaclViolation[] {
  if (!data || typeof data !== 'object') return []
  const report = (data as Record<string, unknown>).validationReport
  if (!report || typeof report !== 'object') return []
  const results = (report as Record<string, unknown>).results
  if (!Array.isArray(results)) return []
  return results.map(toViolation).filter((v): v is ShaclViolation => v !== null)
}

function toViolation(raw: unknown): ShaclViolation | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const focusNode = readIriField(r.focusNode)
  if (!focusNode) return null
  return {
    focusNode,
    resultPath: readIriField(r.resultPath),
    message: readMessageField(r.message) ?? '(no message)',
    sourceShape: readIriField(r.sourceShape),
    severity: readSeverity(r.resultSeverity),
  }
}

function readIriField(field: unknown): string | null {
  if (!field) return null
  if (typeof field === 'string') return field
  if (typeof field === 'object' && 'value' in (field as Record<string, unknown>)) {
    const v = (field as Record<string, unknown>).value
    return typeof v === 'string' ? v : null
  }
  return null
}

function readMessageField(field: unknown): string | null {
  if (!field) return null
  if (typeof field === 'string') return field
  if (Array.isArray(field) && field.length > 0) {
    return readIriField(field[0])
  }
  return readIriField(field)
}

function readSeverity(field: unknown): 'Violation' | 'Warning' | 'Info' {
  const raw = readIriField(field)
  if (!raw) return 'Violation'
  const last = raw.split(/[#/]/).pop() ?? 'Violation'
  if (last === 'Warning') return 'Warning'
  if (last === 'Info') return 'Info'
  return 'Violation'
}
